import { type FC, type ReactNode, useRef, useState } from 'react';

import { type IElectionData } from '../../../../interface/IAgents';
import { type IPartyCustomization } from '../../../../interface/ICustomization';
import { darken, hexToRgb, onBrandColor } from '../../../../theme/applyPartyTheme';
import {
	HeroActions,
	HeroBannerImg,
	HeroBrandRow,
	HeroCandidate,
	HeroCandidatePhoto,
	HeroContent,
	HeroCtaButton,
	HeroEyebrow,
	HeroHeadline,
	HeroInner,
	HeroLogo,
	HeroRoot,
	HeroScrim,
	HeroSocialLink,
	HeroSocials,
	HeroSubheadline,
} from './BrandedHero.styled';
import TrustedInviteNotice from './TrustedInviteNotice';

// Mirrors the (module-scoped) helper in create/index.tsx so the hero can build
// the same "{ELECTION NAME} · {DATE}" eyebrow without exporting from there.
const formatElectionEyebrow = (name?: string | null, dateIso?: string | null): string => {
	const parts: string[] = [];
	if (name) parts.push(name);
	if (dateIso) {
		try {
			const d = new Date(dateIso);
			parts.push(d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }));
		} catch {
			// best-effort — skip the date if it can't be parsed
		}
	}
	return parts.join(' · ').toUpperCase();
};

const FALLBACK_PRIMARY = '#1e5631';

const prefersReducedMotion = (): boolean =>
	typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Compact brand glyphs for the socials row. `currentColor` inherits the hero's
// on-brand text colour so they stay legible on any party background.
const GlobeIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='1.8'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<circle
			cx='12'
			cy='12'
			r='9'
		/>
		<path d='M3 12h18M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18' />
	</svg>
);

const FacebookIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='currentColor'
		aria-hidden='true'
	>
		<path d='M14 9h2.5V6H14c-2 0-3.5 1.5-3.5 3.5V11H8v3h2.5v7h3v-7H16l.5-3h-3V9.5C13.5 9.2 13.7 9 14 9Z' />
	</svg>
);

const TwitterIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='currentColor'
		aria-hidden='true'
	>
		<path d='M17.5 3h3l-6.6 7.5L22 21h-5.9l-4.2-5.4L6.9 21H3.8l7-8L2.5 3h6l3.8 5 4.2-5Zm-1 16 1.7-.1L7.6 4.8H5.8L16.5 19Z' />
	</svg>
);

const InstagramIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='1.8'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<rect
			x='3'
			y='3'
			width='18'
			height='18'
			rx='5'
		/>
		<circle
			cx='12'
			cy='12'
			r='4'
		/>
		<circle
			cx='17'
			cy='7'
			r='1'
			fill='currentColor'
			stroke='none'
		/>
	</svg>
);

const YoutubeIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='currentColor'
		aria-hidden='true'
	>
		<path d='M22 8.2a3 3 0 0 0-2.1-2.1C18 5.6 12 5.6 12 5.6s-6 0-7.9.5A3 3 0 0 0 2 8.2 31 31 0 0 0 1.7 12 31 31 0 0 0 2 15.8a3 3 0 0 0 2.1 2.1c1.9.5 7.9.5 7.9.5s6 0 7.9-.5a3 3 0 0 0 2.1-2.1 31 31 0 0 0 .3-3.8 31 31 0 0 0-.3-3.8ZM10 15V9l5.2 3L10 15Z' />
	</svg>
);

const WhatsAppIcon = () => (
	<svg
		viewBox='0 0 24 24'
		fill='currentColor'
		aria-hidden='true'
	>
		<path d='M12 2a10 10 0 0 0-8.5 15.2L2 22l4.9-1.5A10 10 0 1 0 12 2Zm0 18a8 8 0 0 1-4.1-1.1l-.3-.2-2.9.9.9-2.8-.2-.3A8 8 0 1 1 12 20Zm4.4-6c-.2-.1-1.4-.7-1.6-.8s-.4-.1-.5.1-.6.8-.8 1-.3.2-.5.1a6.5 6.5 0 0 1-3.2-2.8c-.2-.4.2-.4.6-1.2a.4.4 0 0 0 0-.4l-.8-1.9c-.2-.5-.4-.4-.5-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-1 2.2 5.3 5.3 0 0 0 1.1 2.8 12 12 0 0 0 4.6 4c2.3.9 2.3.6 2.7.6a2.5 2.5 0 0 0 1.6-1.2 2 2 0 0 0 .2-1.2c-.1-.1-.3-.2-.5-.3Z' />
	</svg>
);

interface SocialEntry {
	key: string;
	url: string;
	label: string;
	icon: ReactNode;
}

interface BrandedHeroProps {
	customization: IPartyCustomization;
	election?: IElectionData;
	// Opened from the party's trusted link — renders the on-brand notice.
	trustedInvite?: boolean;
	partyName?: string | null;
}

const BrandedHero: FC<BrandedHeroProps> = ({ customization, election, trustedInvite = false, partyName }) => {
	const rootRef = useRef<HTMLDivElement>(null);
	const [logoBroken, setLogoBroken] = useState(false);
	const [candidateBroken, setCandidateBroken] = useState(false);

	// Colour foundation — validated party primary, or the house green as a
	// safety net. Computed here (not just via CSS vars) so the hero is correct on
	// first paint, before applyPartyTheme's effect has run.
	const primary =
		customization.primaryColor && hexToRgb(customization.primaryColor) ? customization.primaryColor : FALLBACK_PRIMARY;
	const onColor = onBrandColor(primary);
	const rgba = (hex: string, alpha: number): string => {
		const c = hexToRgb(hex);
		return c ? `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha})` : hex;
	};

	const hasBanner = Boolean(customization.bannerImageUrl);
	const eyebrow = formatElectionEyebrow(election?.name, election?.date);
	const headline = customization.headline ?? 'Verify your identity';
	const subheadline =
		customization.subheadline ??
		customization.welcomeMessage ??
		'We’ll quickly confirm you’re a real, registered voter — takes about a minute.';
	const ctaText = customization.callToActionText;

	const showCandidate = Boolean(customization.showCandidatePhoto && customization.candidatePhotoUrl) && !candidateBroken;
	const showLogo = Boolean(customization.logoUrl) && !logoBroken;

	const socials: SocialEntry[] = customization.showSocialLinks
		? (
				[
					{ key: 'website', url: customization.websiteUrl, label: 'Website', icon: <GlobeIcon /> },
					{ key: 'facebook', url: customization.facebookUrl, label: 'Facebook', icon: <FacebookIcon /> },
					{ key: 'twitter', url: customization.twitterUrl, label: 'X (Twitter)', icon: <TwitterIcon /> },
					{ key: 'instagram', url: customization.instagramUrl, label: 'Instagram', icon: <InstagramIcon /> },
					{ key: 'youtube', url: customization.youtubeUrl, label: 'YouTube', icon: <YoutubeIcon /> },
					{ key: 'whatsapp', url: customization.whatsAppUrl, label: 'WhatsApp', icon: <WhatsAppIcon /> },
				] as { key: string; url: string | null; label: string; icon: ReactNode }[]
			).filter((s): s is SocialEntry => Boolean(s.url))
		: [];

	// The CTA anchors the hero to the form directly beneath it.
	const onCtaClick = () => {
		const next = rootRef.current?.nextElementSibling;
		next?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
	};

	// Banner scrim: strong party wash on the text side, fading toward the banner
	// on the right. No banner → a solid party gradient block.
	const scrimStyle = {
		background: `linear-gradient(90deg, ${rgba(primary, 0.94)} 0%, ${rgba(primary, 0.78)} 48%, ${rgba(darken(primary, 0.12), 0.35)} 100%)`,
	};
	const rootStyle = {
		color: onColor,
		...(hasBanner ? {} : { background: `linear-gradient(135deg, ${primary} 0%, ${darken(primary, 0.24)} 100%)` }),
	};

	return (
		<HeroRoot
			ref={rootRef}
			style={rootStyle}
		>
			{hasBanner && (
				<>
					<HeroBannerImg
						src={customization.bannerImageUrl ?? ''}
						alt=''
						aria-hidden='true'
						loading='eager'
						decoding='async'
						width={1280}
						height={360}
					/>
					<HeroScrim
						aria-hidden='true'
						style={scrimStyle}
					/>
				</>
			)}

			<HeroInner>
				<HeroContent>
					{(showLogo || eyebrow) && (
						<HeroBrandRow>
							{showLogo && (
								<HeroLogo
									src={customization.logoUrl ?? ''}
									alt=''
									aria-hidden='true'
									loading='eager'
									decoding='async'
									width={56}
									height={56}
									onError={() => setLogoBroken(true)}
								/>
							)}
							{eyebrow && <HeroEyebrow>{eyebrow}</HeroEyebrow>}
						</HeroBrandRow>
					)}

					{trustedInvite && (
						<TrustedInviteNotice
							variant='on-brand'
							partyName={partyName}
						/>
					)}

					<HeroHeadline>{headline}</HeroHeadline>

					{subheadline && <HeroSubheadline>{subheadline}</HeroSubheadline>}

					{(ctaText || socials.length > 0) && (
						<HeroActions>
							{ctaText && (
								<HeroCtaButton
									type='button'
									onClick={onCtaClick}
									style={{ background: primary, color: onColor }}
								>
									{ctaText}
								</HeroCtaButton>
							)}
							{socials.length > 0 && (
								<HeroSocials>
									{socials.map((social) => (
										<HeroSocialLink
											key={social.key}
											href={social.url}
											target='_blank'
											rel='noopener noreferrer'
											aria-label={social.label}
											title={social.label}
										>
											{social.icon}
										</HeroSocialLink>
									))}
								</HeroSocials>
							)}
						</HeroActions>
					)}
				</HeroContent>

				{showCandidate && (
					<HeroCandidate>
						<HeroCandidatePhoto
							src={customization.candidatePhotoUrl ?? ''}
							alt={election?.name ? `Candidate for ${election.name}` : 'Party candidate'}
							loading='eager'
							decoding='async'
							width={200}
							height={260}
							onError={() => setCandidateBroken(true)}
						/>
					</HeroCandidate>
				)}
			</HeroInner>
		</HeroRoot>
	);
};

export default BrandedHero;
